import { z } from 'zod';
// Per-day rolling paid schedule (capability gap from the recap
// scorecard: "per-day rolling paid plans"). Sibling to paidStrategy
// — strategy is the WHAT (angles to test), schedule is the WHEN
// (which days at what budget level).
//
// Why this shape:
//   - Strategy = stable for ~14d, refreshed on exhaustion
//   - Schedule = rolling 7d window, regenerated weekly
//   - Each day's row names per-deliverable daily budgets so the
//     applier can write them to the platforms in one daily tick
//
// Mag's edge here vs "set it once and forget":
//   - Lean budget into days with proven peak engagement signal
//   - Pull back on days that historically waste spend
//   - Sequence angles when the founder wants A/B testing rhythm
//
// Real money guardrails (enforced in the agent prompt + parse):
//   - No per-deliverable day-over-day swing >50% — avoids platform
//     ML re-learning costs and big-spend-day surprises
//   - Total daily spend stays within ±10% of current sum (mag
//     redistributes, doesn't unilaterally increase total budget)
//   - Per-deliverable daily minimum is $1 (platform floor)
//
// A0 IS reachable for this decision type (`meta_paid_plan`), but
// the existing autopilot toggle wires meta_paid_plan=A1 by default
// — so practically schedules apply only when the founder
// explicitly graduates meta_paid_plan to A0, OR runs the founder-
// triggered draft endpoint each week.
/** Per-deliverable budget for one day. The applier walks these on
 *  each daily tick and pushes the changes to the platform via the
 *  existing per-channel budget mutation helpers. */
export const paidScheduleDayDeliverableSchema = z.object({
    deliverableId: z.string().min(1).max(64),
    /** USD whole dollars. Platform floor is $1; cap matches the
     *  per-deliverable cap on meta_paid_ad/google_search_ad
     *  daily_budget_usd. */
    dailyBudgetUsd: z.number().int().min(1).max(10_000),
    /** Mag-voice line on why this day at this budget. Short — the
     *  recap surfaces these inline so the founder reads them as a
     *  sentence per day. ≤200 chars. */
    rationale: z.string().min(1).max(200),
});
/** One day in the rolling schedule. */
export const paidScheduleDaySchema = z.object({
    /** YYYY-MM-DD in brand tz. Days[] is ordered oldest-first. */
    date: z.string().min(8).max(10),
    /** Per-deliverable budget. Same set across all days for a given
     *  schedule (mag varies the BUDGET, not which deliverables run).
     *  Empty when mag wants the day "off" — applier still pushes
     *  $1/day to keep the ad eligible to spend without zeroing the
     *  platform record. */
    perDeliverable: z.array(paidScheduleDayDeliverableSchema).max(20),
    /** Mag-voice one-sentence summary of THIS day's choices. Shown
     *  on the campaign detail page as a per-day eyebrow. */
    dayRationale: z.string().min(1).max(280),
});
export const paidScheduleOutputSchema = z.object({
    /** Channel scope of this schedule. Currently each schedule is
     *  per-channel — a campaign with both meta + google paid
     *  deliverables ends up with TWO schedule blocks (one for each)
     *  rather than mixing them. Simpler to apply + easier for the
     *  founder to read. */
    channel: z.enum(['meta_paid', 'google_paid']),
    /** One-sentence summary in mag's voice — title line for the
     *  schedule UI + recap surface. */
    summary: z.string().min(1).max(280),
    /** Days[]. 7 entries today (a one-week rolling plan); the
     *  schema allows up to 14 for future "two-week look-ahead"
     *  iterations without a schema bump. */
    days: z.array(paidScheduleDaySchema).min(7).max(14),
    /** ISO timestamp the schedule was generated. The applier checks
     *  this to detect stale schedules (>14d old) and skip applying. */
    generatedAt: z.string(),
    /** LLM cost for transparency, same as paidStrategy.costUsd. */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=paid-schedule.js.map